// schemas.ts
import { z } from "zod";

export const step1Schema = z
    .object({
        email: z.string().email({ message: "Invalid email address" }),
        password: z.string().min(6, { message: "Password must be at least 6 characters" }),
        confirmPassword: z.string(),
        phone: z.string().optional(),
        fullname: z.string().min(1, { message: "Full name is required" }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"], 
    });

export const step2Schema = z.object({
  brandName: z.string().min(1, { message: "Brand name is required" }),
  brandWebsite: z.string().refine(
    (val) => {
      try {
        const hasProtocol = val.includes("://");
        // If it has a protocol, validate that it's only http or https
        if (hasProtocol) {
          const url = new URL(val);
          return url.protocol === "http:" || url.protocol === "https:";
        }
        // If no protocol, make sure it's a valid domain
        return /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val);
      } catch {
        return false;
      }
    },
    {
      message: "Must be a valid domain or HTTP/HTTPS URL",
    },
  ),
  brandCountry: z.string().min(1, { message: "Brand country is required" }),
  currentFocus: z.string()
  .max(240, { message: "Maximum 240 characters" })
  .refine(
    (val) => {
      const words = val.trim().split(/\s+/).filter(Boolean);
      return words.length <= 40;
    },
    { message: "Maximum 40 words." }
  )
  .refine(
    (str) => {
      const letterMatches = str.match(/\p{L}/gu);
      const letterCount = letterMatches ? letterMatches.length : 0;
      return str.length === 0 || letterCount >= 10;
    },
    {
      message:
        "Please add a bit more detail (e.g., product + audience + what you want to rank for).",
    }
  )
  .optional(),
});

export const step3Schema = z.object({
  clusters: z.any()
});

export const step4Schema = z
  .object({
    clusters: z
      .array(
        z.object({
          title: z.string(),
          clusterId: z.string().optional(),
          prompts: z.array(
            z.object({
              text: z.string(),
              country: z.string(),
              isCustom: z.boolean().optional(),
            }),
          ),
        }),
      )
      .min(1, "At least 1 cluster required"),
  })
  .refine((data) => {
    const total = data.clusters.reduce(
      (sum, cluster) => sum + cluster.prompts.length,
      0,
    );
    return total >= 1;
  }, {
    message: "At least 1 prompt required",
    path: ["clusters"],
  })
  .refine((data) => {
    const total = data.clusters.reduce(
      (sum, cluster) => sum + cluster.prompts.length,
      0,
    );
    return total <= 16;
  }, {
    message: "No more than 16 prompts allowed",
    path: ["clusters"],
  });
